import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { clearCoverageCell, getCoverageCell, getCoverageMatrix } from 'features/coverage/coverageSlice'
import { fetchCoverageRegistries } from './coverageRegistryFetch'
import { ICoverageMatrixCell, ICoverageMatrixShift } from 'types/api/coverageApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { JustSpinner } from 'components/Loading/Loading'
import { PageWrapper, Flex } from 'styling/GlobalStyles'
import { Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { coverageTone } from 'utils/coverageStatus'
import { coverageSites } from './coverageDemoStore'
import {
  HeaderControls,
  HistoricalBanner,
  StripWrap,
  LabelsCol,
  LaneLabel,
  RulerSpacer,
  TracksCol,
  LaneTrack,
  StripBar,
  RulerTrack,
  StripTickLabel,
  NowLine,
  MatrixScroll,
  MatrixTable,
  MatrixHeadCell,
  DeptCell,
  DeptCellName,
  DeptCellSub,
  Cell,
  EmptyCellBox,
  CellTop,
  CellNumbers,
  CellDot,
  CritBadge,
  CellSub,
  LegendRow,
  LegendSwatch,
  LegendDashed,
  Scrim,
  Panel,
  PanelHeader,
  PanelBody,
  PanelClose,
  SectionLabel,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  PersonRow,
  PersonName,
  PersonRole,
  VacantRow,
  GapCallout,
  PanelActions,
  EmptyState,
} from './CoverageMatrix.styled'

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

const pct = (min: number) => (min / 1440) * 100

const phaseSub = (cell: ICoverageMatrixCell) =>
  cell.phase === 'future' ? 'confirmed roster' : cell.phase === 'active' ? 'attendance' : 'closed'

const PERSON_PILL: { [status: string]: 'success' | 'warning' | 'danger' | 'info' | 'neutral' } = {
  present: 'success',
  late: 'warning',
  break: 'info',
  absent: 'danger',
  confirmed: 'success',
  'no response': 'warning',
  unavailable: 'danger',
}

export const CoverageMatrix = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { matrix, cell, isLoading } = useSelector((state: RootState) => state.coverage)
  const [siteId, setSiteId] = useState(1)
  const [date, setDate] = useState<Date>(new Date())

  const dateStr = date.toISOString().slice(0, 10)
  const todayStr = new Date().toISOString().slice(0, 10)
  const isToday = dateStr === todayStr
  const isPastDate = dateStr < todayStr

  useEffect(() => {
    fetchCoverageRegistries(dispatch).then(() =>
      dispatch(getCoverageMatrix({ siteNodeId: siteId, date: dateStr })),
    )
  }, [dispatch, siteId, dateStr])

  useEffect(() => {
    const sites = coverageSites()
    if (sites.length && !sites.some((s) => s.id === siteId)) setSiteId(sites[0].id)
  }, [matrix, siteId])

  const visibleShifts = useMemo(() => {
    if (!matrix) return []
    return matrix.shifts.filter((s) =>
      matrix.departments.some((d) => d.cells.some((c) => c.shiftId === s.shiftId)),
    )
  }, [matrix])

  const nowPct = useMemo(() => {
    const now = new Date()
    return pct(now.getHours() * 60 + now.getMinutes())
  }, [])

  const openCell = (departmentId: number, shiftId: number) =>
    dispatch(getCoverageCell({ siteNodeId: siteId, departmentId, shiftId, date: dateStr }))

  const closeCell = () => dispatch(clearCoverageCell())

  const stripBars = (shift: ICoverageMatrixShift) => {
    const from = toMin(shift.windowFrom)
    const to = toMin(shift.windowTo)
    if (to < from) {
      return [
        { left: pct(from), width: pct(1440 - from) },
        { left: 0, width: pct(to) },
      ]
    }
    return [{ left: pct(from), width: pct(to - from) }]
  }

  const shiftActiveNow = (shift: ICoverageMatrixShift) => {
    if (!isToday) return false
    const now = new Date()
    const nowMin = now.getHours() * 60 + now.getMinutes()
    const from = toMin(shift.windowFrom)
    const to = toMin(shift.windowTo)
    return to < from ? nowMin >= from || nowMin < to : nowMin >= from && nowMin < to
  }

  const posts = cell?.rowsBreakdown.filter((r) => r.locationName) || []
  const plainRows = cell?.rowsBreakdown.filter((r) => !r.locationName) || []
  const vacantCount = cell ? Math.max(cell.required - cell.covered, 0) : 0

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Coverage
          </Head1>
          <Button variant='secondary' onClick={() => navigate('/hr/schedule/coverage-models')}>
            Coverage Models ›
          </Button>
        </Flex>
        <OtherText grey>Schedule › Coverage · covered vs required per department and shift</OtherText>

        <HeaderControls>
          <CalendarInput
            nomarg
            width='220px'
            labelText='Date'
            defaultValue={date}
            onChange={(d: Date | null) => d && setDate(d)}
          />
          <HonestSelectInput
            width='200px'
            nomarg
            labelText='Site'
            options={coverageSites().map((s) => ({ value: s.id, label: s.name }))}
            defaultValue={siteId}
            onChange={(v: ISelectValue) => setSiteId(Number(v.value))}
          />
        </HeaderControls>

        {isPastDate && <HistoricalBanner>Historical view — {dateStr}</HistoricalBanner>}

        {isLoading && !matrix ? (
          <Flex justcenter mt='32'>
            <JustSpinner />
          </Flex>
        ) : !matrix || !matrix.departments.length ? (
          <EmptyState>
            <TitleText>No coverage models on {matrix?.siteName || 'this site'} yet.</TitleText>
            <Button variant='primary' onClick={() => navigate('/hr/schedule/coverage-models')}>
              Set required staffing per department →
            </Button>
          </EmptyState>
        ) : (
          <>
            <StripWrap>
              <LabelsCol>
                {visibleShifts.map((s) => (
                  <LaneLabel key={s.shiftId} $dim={!shiftActiveNow(s)}>
                    {s.name}
                  </LaneLabel>
                ))}
                <RulerSpacer />
              </LabelsCol>
              <TracksCol>
                {visibleShifts.map((s) => (
                  <LaneTrack key={s.shiftId}>
                    {stripBars(s).map((b, i) => (
                      <StripBar key={i} $left={b.left} $width={b.width} $dim={!shiftActiveNow(s)}>
                        {b.width > 12 ? `${s.windowFrom}–${s.windowTo}` : ''}
                      </StripBar>
                    ))}
                  </LaneTrack>
                ))}
                <RulerTrack>
                  {[0, 6, 12, 18, 24].map((h) => (
                    <StripTickLabel key={h} $pct={pct(h * 60)} $edge={h === 0 ? 'start' : h === 24 ? 'end' : undefined}>
                      {String(h % 24).padStart(2, '0')}:00
                    </StripTickLabel>
                  ))}
                </RulerTrack>
                {isToday && <NowLine $pct={nowPct} />}
              </TracksCol>
            </StripWrap>

            <MatrixScroll>
              <MatrixTable>
                <thead>
                  <tr>
                    <MatrixHeadCell>Department</MatrixHeadCell>
                    {visibleShifts.map((s) => (
                      <MatrixHeadCell key={s.shiftId}>
                        {s.name} · {s.windowFrom}–{s.windowTo}
                      </MatrixHeadCell>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.departments.map((d) => {
                    const dayCovered = d.cells.reduce((a, c) => a + c.covered, 0)
                    const dayRequired = d.cells.reduce((a, c) => a + c.required, 0)
                    return (
                    <tr key={d.departmentId}>
                      <DeptCell>
                        <DeptCellName>{d.name}</DeptCellName>
                        <DeptCellSub>
                          {dayCovered}/{dayRequired} today
                        </DeptCellSub>
                      </DeptCell>
                      {visibleShifts.map((s) => {
                        const c = d.cells.find((x) => x.shiftId === s.shiftId)
                        if (!c) return <EmptyCellBox key={s.shiftId}>—</EmptyCellBox>
                        const tone = coverageTone(c)
                        return (
                          <Cell
                            key={s.shiftId}
                            $phase={c.phase}
                            $clickable
                            onClick={() => openCell(d.departmentId, s.shiftId)}
                          >
                            <CellTop>
                              <CellDot $tone={tone} />
                              <CellNumbers $tone={tone}>
                                {c.covered}/{c.required}
                              </CellNumbers>
                              {c.criticalGap && <CritBadge>CRIT</CritBadge>}
                            </CellTop>
                            <CellSub>{phaseSub(c)}</CellSub>
                          </Cell>
                        )
                      })}
                    </tr>
                    )
                  })}
                </tbody>
              </MatrixTable>
            </MatrixScroll>

            <LegendRow>
              <span>
                <LegendSwatch $color={tokens.status.success.fg} /> covered
              </span>
              <span>
                <LegendSwatch $color={tokens.status.warning.fg} /> under target
              </span>
              <span>
                <LegendSwatch $color={tokens.status.danger.fg} /> critical gap
              </span>
              <span>
                <LegendDashed /> dashed = future → roster confirmations, not attendance
              </span>
            </LegendRow>
          </>
        )}
      </PageWrapper>

      {cell && (
        <>
          <Scrim onClick={closeCell} />
          <Panel>
            <PanelHeader>
              <div>
                <TitleText weight='bold'>
                  {cell.departmentName} — {cell.shiftName} ({cell.windowFrom}–{cell.windowTo})
                </TitleText>
                <Flex gap='0.5rem' mt='8' center>
                  <StatusPill status={cell.phase === 'active' ? 'info' : 'neutral'}>
                    {cell.phase === 'future' ? 'Future · confirmed roster' : cell.phase === 'active' ? 'Active · attendance' : 'Past · closed'}
                  </StatusPill>
                  <StatusPill status={coverageTone(cell) === 'neutral' ? 'neutral' : coverageTone(cell)} dot>
                    {cell.covered}/{cell.required}
                  </StatusPill>
                </Flex>
              </div>
              <PanelClose type='button' aria-label='Close' onClick={closeCell}>
                <CloseIcon />
              </PanelClose>
            </PanelHeader>
            <PanelBody>
              {cell.criticalGap && <GapCallout>Critical position unfilled — escalates at T−4h.</GapCallout>}

              {plainRows.length > 0 && (
                <div>
                  <SectionLabel>Roles</SectionLabel>
                  {plainRows.map((r, i) => (
                    <BreakdownRow key={i}>
                      <BreakdownLabel>{r.label}</BreakdownLabel>
                      {r.critical && <CritBadge>CRITICAL</CritBadge>}
                      <BreakdownValue $tone={coverageTone({ required: r.required, covered: r.covered, criticalGap: r.critical && r.covered < r.required })}>
                        {cell.phase === 'future' ? `${r.covered} / ${cell.rostered ?? r.required} / ${r.required}` : `${r.covered}/${r.required}`}
                      </BreakdownValue>
                    </BreakdownRow>
                  ))}
                </div>
              )}

              {posts.length > 0 && (
                <div>
                  <SectionLabel>Posts</SectionLabel>
                  {posts.map((r, i) => (
                    <BreakdownRow key={i}>
                      <BreakdownLabel>{r.label}</BreakdownLabel>
                      {r.critical && <CritBadge>CRITICAL</CritBadge>}
                      <BreakdownValue $tone={coverageTone({ required: r.required, covered: r.covered, criticalGap: r.critical && r.covered < r.required })}>
                        {r.covered}/{r.required}
                      </BreakdownValue>
                    </BreakdownRow>
                  ))}
                </div>
              )}

              <div>
                <SectionLabel>People</SectionLabel>
                {cell.people.map((p) => (
                  <PersonRow key={p.userId}>
                    <PersonName>{p.name}</PersonName>
                    <PersonRole>
                      {p.roleLabel}
                      {p.postName ? ` @ ${p.postName}` : ''}
                    </PersonRole>
                    <StatusPill status={PERSON_PILL[p.status] || 'neutral'} dot>
                      {p.status}
                    </StatusPill>
                  </PersonRow>
                ))}
                {Array.from({ length: vacantCount }, (_, i) => (
                  <VacantRow key={`v-${i}`}>— vacant —</VacantRow>
                ))}
              </div>
            </PanelBody>
            <PanelActions>
              <Button variant='primary' onClick={() => toast.success('Cover request sent to the Needs-You queue')}>
                Request cover
              </Button>
              <Button variant='secondary' onClick={() => navigate('/hr/schedule')}>
                Reassign from another shift
              </Button>
              {cell.phase === 'future' && (
                <Button variant='secondary' onClick={() => toast.success('Confirmation reminder sent to unconfirmed people')}>
                  Send confirmation reminder
                </Button>
              )}
            </PanelActions>
          </Panel>
        </>
      )}
    </>
  )
}
